import { useState } from "react";
import { useT } from "../i18n";

// Bulk edit dialog (UX phase 3): apply common changes to N selected items — add/remove tags, move
// to a folder, and flip the offline-cache flag. It only collects intent; the parent executes each
// change (re-encrypting tag edits per item via patchItem, moving via the move API). Fields left
// untouched are no-ops, so a partial edit (e.g. only add a tag) leaves everything else as-is.
export interface BulkEdit {
  addTags: string[];
  removeTags: string[];
  moveFolderId: string | null | undefined; // undefined = don't move; null = root; string = folder id
  offline: boolean | null; // null = leave unchanged
}

const parseTags = (s: string): string[] => s.split(",").map((t) => t.trim()).filter(Boolean);

export function BulkEditDialog({
  count,
  folders,
  onClose,
  onApply,
}: {
  count: number;
  folders: { id: string; name: string; depth: number }[];
  onClose: () => void;
  onApply: (edit: BulkEdit) => void;
}) {
  const t = useT();
  const [addTags, setAddTags] = useState("");
  const [removeTags, setRemoveTags] = useState("");
  const [move, setMove] = useState<"" | "__root__" | string>(""); // "" = don't move
  const [offline, setOffline] = useState<"" | "on" | "off">(""); // "" = leave

  const add = parseTags(addTags);
  const remove = parseTags(removeTags);
  const moveFolderId = move === "" ? undefined : move === "__root__" ? null : move;
  const offlineVal = offline === "" ? null : offline === "on";
  const hasChange = add.length > 0 || remove.length > 0 || moveFolderId !== undefined || offlineVal !== null;

  function apply() {
    if (!hasChange) return;
    onApply({ addTags: add, removeTags: remove, moveFolderId, offline: offlineVal });
  }

  const input = "w-full rounded border border-border bg-panel2 px-2 py-1.5 text-sm outline-none focus:border-accent";
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60" onClick={onClose}>
      <div className="flex w-[460px] flex-col rounded-md border border-border bg-panel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 font-semibold">
          {t("Bulk edit")} · {count} {t("selected")}
        </div>
        <div className="flex flex-col gap-3 px-4 py-4 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t("Add tags (comma-separated)")}</span>
            <input className={input} value={addTags} onChange={(e) => setAddTags(e.target.value)} placeholder="prod, web" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t("Remove tags (comma-separated)")}</span>
            <input className={input} value={removeTags} onChange={(e) => setRemoveTags(e.target.value)} placeholder="legacy" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t("Move to folder")}</span>
            <select className={input} value={move} onChange={(e) => setMove(e.target.value)}>
              <option value="">{t("(don't move)")}</option>
              <option value="__root__">{t("(root)")}</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {"— ".repeat(f.depth)}
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">{t("Offline cache")}</span>
            <select className={input} value={offline} onChange={(e) => setOffline(e.target.value as "" | "on" | "off")}>
              <option value="">{t("Leave unchanged")}</option>
              <option value="on">{t("Enable offline cache")}</option>
              <option value="off">{t("Disable offline cache")}</option>
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 text-sm hover:border-accent">
            {t("Cancel")}
          </button>
          <button
            onClick={apply}
            disabled={!hasChange}
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-50"
          >
            {t("Apply to")} {count}
          </button>
        </div>
      </div>
    </div>
  );
}
